import React, { useEffect, useState } from 'react';
import appConfig from './config';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './components/layout/Layout';
import Users from './pages/Users';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import AdminDashboard from './pages/AdminDashboard';
import LoginPage from './pages/Login';
import Wallet from './components/WalletUpdate';
import Deposit from './pages/Deposit';
import Withdraw from './pages/Withdraw';
import House from './pages/House'
import History from './pages/History';
import Game from './pages/Games';
import GamesLog from './pages/WinSetting';
import SEO from './pages/SeoManager';
import Referral from './pages/Referral';
import NotificationSettings from './pages/NotificationSettings';
import WinSettings from './pages/WinSetting';
import Payments from './pages/Payment';
import ManualDeposit from './pages/ManualDeposit';
import Withdrawals from './pages/Withdrawals';
import ChangePassword from './pages/ChangePassword';
import GameControl from './pages/GameControl';


import RoleManagement from './pages/RoleManagement';
import TeamManagement from './pages/TeamManagement';

import UserProfile from './pages/UserProfile';
import UserReports from './pages/UserReports';
import FinancialReports from './pages/FinancialReports';
import GameHistoryReports from './pages/GameHistoryReports';
import ProfitAnalytics from './pages/ProfitAnalytics';
import Aggregators from './pages/GameEngine/Aggregators';
import Providers from './pages/GameEngine/Providers';
import Groups from './pages/GameEngine/Groups';
import Categories from './pages/GameEngine/Categories';
import GamesManagement from './pages/GameEngine/GamesManagement';
import AuthConfigs from './pages/AuthConfigs';
import GatewayConfigs from './pages/GatewayConfigs';
import CampaignBuilder from './pages/CampaignBuilder';
import CmsPageManager from './pages/CmsPageManager';
import AffiliateSettings from './pages/AffiliateSettings';
import VipLevels from './pages/VipLevels';


import axios from 'axios';

import apiClient from './services/apiClient';

// Setup global axios interceptor for admin refresh token
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (originalRequest.url && (originalRequest.url.includes('/admin/login') || originalRequest.url.includes('/login'))) {
        return Promise.reject(error);
      }
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (refreshToken) {
        try {
          const res = await axios.post(`${appConfig.baseURL}/api/auth/refresh`, {
            refreshToken
          });
          
          if (res.data && res.data.token) {
            const newToken = res.data.token;
            localStorage.setItem('token', newToken);
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        } catch (err) {
          console.error('Refresh token failed:', err);
        }
      }
      // If we reach here, either there was no refresh token, or the refresh request failed
      const role = (localStorage.getItem('role') || '').toLowerCase();
      localStorage.clear();
      if (role === 'affiliate') {
        window.location.href = '/login/affiliate';
      } else if (role === 'subadmin' || role === 'manager') {
        window.location.href = '/login/manager';
      } else {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const token = localStorage.getItem('token');
  const [isTestingMode, setIsTestingMode] = useState(true);

  // ── Fetch brand config from DB on startup ──────────────────────────────
  useEffect(() => {
    fetch(`${appConfig.baseURL}/website-config`)
      .then(r => r.json())
      .then((data: any) => {
        if (!data) return;
        const configData = data.success ? data.data : data;

        setIsTestingMode(configData.is_testing_mode !== false);
        // Store globally so any component can access
        (window as any).adminSiteConfig = configData;

        // Set page title
        if (configData.website_name && !window.location.pathname.startsWith('/login')) {
          const role = (localStorage.getItem('role') || 'Admin').toLowerCase();
          const roleDisplayName = role === 'admin' ? 'Admin' : role.charAt(0).toUpperCase() + role.slice(1);
          document.title = `${configData.website_name} — ${roleDisplayName}`;
        }

        // Set favicon
        if (configData.favicon) {
          const faviconUrl = `${appConfig.baseURL}${configData.favicon}?t=${Date.now()}`;
          let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = faviconUrl;
        }
      })
      .catch(() => {});
  }, []);
  // ───────────────────────────────────────────────────────────────────────

  const NotAuthorized = () => {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] text-center px-4">
        <div className="bg-[#1e293b]/40 backdrop-blur-md border border-slate-800 p-8 rounded-2xl max-w-md shadow-2xl flex flex-col items-center gap-4">
          <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-full text-red-500">
            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-100">403 - Access Denied</h2>
          <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
            You do not have the required permissions to view this page. Please contact your administrator if you believe this is an error.
          </p>
          <button 
            onClick={() => window.location.href = '/'}
            className="mt-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs sm:text-sm font-semibold transition-colors duration-200 cursor-pointer shadow-lg shadow-indigo-600/30"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  };

  const PermissionProtectedRoute = ({ 
    permissionKey, 
    permissionKeys, 
    children 
  }: { 
    permissionKey?: string; 
    permissionKeys?: string[]; 
    children: React.ReactNode 
  }) => {
    const getPermissions = () => {
      const cached = localStorage.getItem("user_permissions");
      if (cached) {
        try {
          return JSON.parse(cached);
        } catch (e) {}
      }
      const token = localStorage.getItem("token");
      if (!token) return {};
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const parsed = JSON.parse(jsonPayload);
        return parsed.user?.permissions || {};
      } catch (e) {
        return {};
      }
    };

    const role = (localStorage.getItem("role") || "").toLowerCase();
    const permissions = getPermissions();

    let isAuthorized = role === 'admin' || role === 'superadmin';
    if (!isAuthorized) {
      if (permissionKey) {
        isAuthorized = !!permissions[permissionKey];
      } else if (permissionKeys) {
        isAuthorized = permissionKeys.some(k => !!permissions[k]);
      }
    }

    if (!isAuthorized) {
      return <NotAuthorized />;
    }

    return <>{children}</>;
  };

  const ProtectedRoutes = () => {
    return token ? <Outlet /> : <Navigate to="/login" replace />;
  };

  return (
    <BrowserRouter>
      <ToastContainer 
        theme="dark" 
        position="top-right" 
        autoClose={3000} 
        hideProgressBar={true} 
        transition={Slide}
        closeButton={false}
        toastClassName="premium-toast"
      />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/:roleParam" element={<LoginPage />} />
        <Route element={<ProtectedRoutes />}>
          <Route path="campaigns/editor" element={<PermissionProtectedRoute permissionKey="campaigns"><CampaignBuilder /></PermissionProtectedRoute>} />
          <Route path="/" element={<Layout />}>
            <Route index element={<PermissionProtectedRoute permissionKey="dashboard"><AdminDashboard /></PermissionProtectedRoute>} />
            <Route path="withdraw" element={<Withdraw />} />
            <Route path="users" element={<PermissionProtectedRoute permissionKey="users"><Users /></PermissionProtectedRoute>} />
            <Route path="notifications" element={<PermissionProtectedRoute permissionKey="notifications"><Notifications /></PermissionProtectedRoute>} />
            <Route path="users/profile/:id" element={<PermissionProtectedRoute permissionKey="users"><UserProfile /></PermissionProtectedRoute>} />
            <Route path="settings" element={<PermissionProtectedRoute permissionKey="settings_general"><Settings /></PermissionProtectedRoute>} />
            <Route path="cms" element={
              <PermissionProtectedRoute permissionKeys={['cms_pages', 'cms_banners', 'cms_logos', 'cms_footer', 'cms_social', 'cms_help', 'cms_game_cards', 'cms_affiliate']}>
                <CmsPageManager />
              </PermissionProtectedRoute>
            } />
            <Route path="roles" element={<PermissionProtectedRoute permissionKey="roles"><RoleManagement /></PermissionProtectedRoute>} />
            <Route path="team" element={<PermissionProtectedRoute permissionKey="team"><TeamManagement /></PermissionProtectedRoute>} />
            <Route path="changePassword" element={<ChangePassword />} />
            <Route path="withdrawals" element={<PermissionProtectedRoute permissionKey="withdrawals"><Withdrawals /></PermissionProtectedRoute>} />
            <Route path="manual-withdrawal" element={<Navigate to="/withdrawals" replace />} />
            <Route path="deposits" element={<PermissionProtectedRoute permissionKey="deposits"><ManualDeposit /></PermissionProtectedRoute>} />
            <Route path="deposit" element={<Deposit />} />
            <Route path="games" element={<Game />} />
            <Route path="win-settings" element={<WinSettings />} />
            <Route path="house" element={<House />} />
            <Route path="game-control" element={<GameControl />} />
            <Route path="wallet" element={<Wallet />} />
            <Route path="history" element={<History />} />
            <Route path="seo-manager" element={<SEO />} />
            <Route path="affiliate" element={<Referral />} />
            <Route path="affiliate-settings" element={<PermissionProtectedRoute permissionKey="affiliate_settings"><AffiliateSettings /></PermissionProtectedRoute>} />

            <Route path="notification-settings" element={<NotificationSettings />} />
            <Route path="auth-configs" element={<PermissionProtectedRoute permissionKey="settings_auth"><AuthConfigs /></PermissionProtectedRoute>} />
            <Route path="gateway-configs" element={<PermissionProtectedRoute permissionKey="settings_gateway"><GatewayConfigs /></PermissionProtectedRoute>} />
            <Route path="campaigns" element={<PermissionProtectedRoute permissionKey="campaigns"><CampaignBuilder /></PermissionProtectedRoute>} />
            <Route path="vip-levels" element={<PermissionProtectedRoute permissionKey="vip_levels"><VipLevels /></PermissionProtectedRoute>} />

            {/* Reports & Analytics */}
            <Route path="reports/users" element={<PermissionProtectedRoute permissionKey="reports_users"><UserReports /></PermissionProtectedRoute>} />
            <Route path="reports/financials" element={<PermissionProtectedRoute permissionKey="reports_financial"><FinancialReports /></PermissionProtectedRoute>} />
            <Route path="reports/game-history" element={<PermissionProtectedRoute permissionKey="reports_games"><GameHistoryReports /></PermissionProtectedRoute>} />
            <Route path="reports/profit" element={<PermissionProtectedRoute permissionKey="reports_financial"><ProfitAnalytics /></PermissionProtectedRoute>} />

            <Route path="game-engine/aggregators" element={<PermissionProtectedRoute permissionKey="game_engine_aggregators"><Aggregators /></PermissionProtectedRoute>} />
            <Route path="game-engine/providers" element={<PermissionProtectedRoute permissionKey="game_engine_providers"><Providers /></PermissionProtectedRoute>} />
            <Route path="game-engine/categories" element={<PermissionProtectedRoute permissionKey="game_engine_categories"><Categories /></PermissionProtectedRoute>} />
            <Route path="game-engine/groups" element={<PermissionProtectedRoute permissionKey="game_engine_groups"><Groups /></PermissionProtectedRoute>} />
            <Route path="game-engine/games" element={<PermissionProtectedRoute permissionKey="game_engine_games"><GamesManagement /></PermissionProtectedRoute>} />
          </Route>
        </Route>
      </Routes>
      <style>{`
        @keyframes adminMarquee {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
      `}</style>
      {isTestingMode && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '28px',
          backgroundColor: '#17181b',
          borderTop: '1px solid rgba(245, 158, 11, 0.35)',
          zIndex: 999999,
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: '11px',
          color: '#9ca3af',
          fontWeight: 500,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.5)',
          userSelect: 'none',
          letterSpacing: '0.3px'
        }}>
          <div style={{
            display: 'flex',
            width: 'max-content',
            animation: 'adminMarquee 35s linear infinite'
          }}>
            <span style={{ padding: '0 60px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', marginRight: '4px' }}>⚠️</span> <strong style={{ color: '#fbbf24' }}>Disclaimer:</strong> This game demo is not intended for users within India. Due to the Gaming Act 2025 regulations, it is provided solely for demonstration purposes to international clients interested in development services.
            </span>
            <span style={{ padding: '0 60px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', marginRight: '4px' }}>⚠️</span> <strong style={{ color: '#fbbf24' }}>Disclaimer:</strong> This game demo is not intended for users within India. Due to the Gaming Act 2025 regulations, it is provided solely for demonstration purposes to international clients interested in development services.
            </span>
          </div>
        </div>
      )}
    </BrowserRouter>
  );
}

export default App;
